// components/NewUi/TreatmentFlow/PatientDetails.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import flow from "./flow.module.scss";
import { toast } from "react-toastify";
import styles from "./patientDetails.module.scss";
import Lottie from "lottie-react";
import generatingAnim from "./animations/Female Employee Working on Data Security.json";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AudioRecorder from "../AudioRecorder/Audio3second";

const normalizeNoteValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch (_err) {
    return String(value);
  }
};

const getNotesFromTreatmentData = (data) => {
  const possibleNote =
    data?.notes ||
    data?.patient_notes ||
    data?.patient_note ||
    data?.clinical_notes ||
    data?.transcription_notes ||
    data?.transcript ||
    data?.transcript_text ||
    data?.audio_recording?.patient_note ||
    data?.audio_recording?.notes ||
    data?.audio_recording?.transcript ||
    "";

  return normalizeNoteValue(possibleNote);
};

const escapePatientNoteHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatPatientNoteInline = (value) => {
  const escapedValue = escapePatientNoteHtml(String(value || "").trim());
  return escapedValue.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
};

const normalizePatientNoteForRichEditor = (value) =>
  String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/^\s*#\s*/, "")
    .replace(
      /\s+\*\*(MH|PCO|SH|O\/e|I\/O|Soft tissues|OH|Gingival health|Hard tissues|Restorations|Crown margins|Toothwear|Abrasion|Abfraction|Acid erosion|Attrition|Occlusion|Mobility|SI|SI for specific tooth|Tooth|TTP|BST|Sinus formation|Colour changes|Diagnosis|Dw pt|Pt has chosen|Risk assessment|Caries risk|Perio risk|Toothwear risk|Recall):\*\*/gi,
      "\n\n**$1:**"
    )
    .replace(/\s+-\s+(?=\*\*[^*]+:\*\*)/g, "\n- ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const splitPatientNoteListItems = (value) => {
  const trimmedValue = String(value || "").trim();
  const hasListMarkers =
    trimmedValue.startsWith("-") || /\s+-\s+(?=\*\*[^*]+:\*\*)/.test(trimmedValue);

  if (!hasListMarkers) return [];

  return trimmedValue
    .split(/\s+-\s+(?=\*\*[^*]+:\*\*)/g)
    .map((item) => item.replace(/^-\s*/, "").trim())
    .filter(Boolean);
};

const renderPatientNoteList = (items) => {
  if (!items.length) return "";

  return `<ul>${items
    .map((item) => `<li>${formatPatientNoteInline(item)}</li>`)
    .join("")}</ul>`;
};

const renderPatientNoteSection = (label, paragraphs = [], listItems = []) => {
  const bodyHtml = [
    ...paragraphs
      .map((paragraph) => String(paragraph || "").trim())
      .filter(Boolean)
      .map((paragraph) => `<p>${formatPatientNoteInline(paragraph)}</p>`),
    renderPatientNoteList(listItems),
  ]
    .filter(Boolean)
    .join("\n");

  return `<section><h4>${escapePatientNoteHtml(label)}:</h4>${bodyHtml}</section>`;
};

const formatPatientNoteHtmlForEditor = (patientNote) => {
  const rawText = normalizeNoteValue(patientNote);

  if (!rawText.trim()) return "";

  const normalizedText = normalizePatientNoteForRichEditor(rawText);
  const lines = normalizedText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return "";

  const htmlParts = [];
  let pendingListItems = [];
  let currentSectionLabel = "";
  let currentSectionParagraphs = [];
  let currentSectionListItems = [];

  const flushList = () => {
    if (!pendingListItems.length) return;
    htmlParts.push(renderPatientNoteList(pendingListItems));
    pendingListItems = [];
  };

  const flushSection = () => {
    if (!currentSectionLabel) return;

    htmlParts.push(
      renderPatientNoteSection(
        currentSectionLabel,
        currentSectionParagraphs,
        currentSectionListItems
      )
    );
    currentSectionLabel = "";
    currentSectionParagraphs = [];
    currentSectionListItems = [];
  };

  lines.forEach((line, index) => {
    const sectionMatch = line.match(/^\*\*([^*]+):\*\*\s*(.*)$/);

    if (index === 0 && !line.startsWith("-") && !sectionMatch) {
      flushSection();
      flushList();
      htmlParts.push(`<h3>${formatPatientNoteInline(line)}</h3>`);
      return;
    }

    if (sectionMatch) {
      flushSection();
      flushList();
      currentSectionLabel = sectionMatch[1];

      const body = sectionMatch[2].trim();
      const bodyListItems = splitPatientNoteListItems(body);

      if (bodyListItems.length) {
        currentSectionListItems.push(...bodyListItems);
      } else if (body) {
        currentSectionParagraphs.push(body);
      }

      return;
    }

    if (line.startsWith("-")) {
      const cleanLine = line.replace(/^-\s*/, "").trim();

      if (currentSectionLabel) {
        currentSectionListItems.push(cleanLine);
      } else {
        pendingListItems.push(cleanLine);
      }
      return;
    }

    if (currentSectionLabel) {
      currentSectionParagraphs.push(line);
      return;
    }

    flushList();
    htmlParts.push(`<p>${formatPatientNoteInline(line)}</p>`);
  });

  flushSection();
  flushList();

  return htmlParts.join("\n");
};

const normalizeEditedPatientNoteText = (value) =>
  String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const PlainPatientNoteEditor = ({
  value,
  onChange,
  disabled = false,
  placeholder = "",
  minHeight = 300,
  ariaLabel = "Patient notes editor",
  className = "",
}) => {
  const editorRef = useRef(null);
  const htmlValue = useMemo(() => formatPatientNoteHtmlForEditor(value), [value]);
  const hasValue = Boolean(String(value || "").trim());

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (document.activeElement === editor) return;

    if (editor.innerHTML !== htmlValue) {
      editor.innerHTML = htmlValue;
    }
  }, [htmlValue]);

  const emitValue = () => {
    const editor = editorRef.current;
    const nextValue = normalizeEditedPatientNoteText(editor?.innerText || "");
    onChange(nextValue);
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData("text/plain") || "";
    document.execCommand("insertText", false, pastedText);
  };

  return (
    <div
      className={`${styles.richNoteEditorShell} ${className || ""}`.trim()}
      style={{ minHeight }}
    >
      {!hasValue && placeholder ? (
        <div className={styles.richNotePlaceholder}>{placeholder}</div>
      ) : null}

      <div
        ref={editorRef}
        className={`${styles.richNoteEditor} ${
          disabled ? styles.richNoteEditorDisabled : ""
        }`.trim()}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emitValue}
        onBlur={emitValue}
        onPaste={handlePaste}
        style={{ minHeight }}
      />
    </div>
  );
};

const DEFAULT_AUDIO_UNSAFE_CLOSE_STATE = {
  isUnsafe: false,
  reason: null,
  title: "",
  message: "",
  isRecording: false,
  isUploadInProgress: false,
  isWorkflowInProgress: false,
};

export default function PatientDetails({
  onNext,
  onBack,
  treatmentData,
  setTreatmentData,
}) {
  const location = useLocation();
  const transcriptionModalOpenedRef = useRef(false);
  const shouldMarkReloadedNoteAsEditedRef = useRef(false);
  const [clinician, setClinician] = useState("");
  const [applyTemplate, setApplyTemplate] = useState(true);
  const [note, setNote] = useState("");
  const [noteEditorVersion, setNoteEditorVersion] = useState(0);
  const [dueOption, setDueOption] = useState("2 weeks");
  const [customDate, setCustomDate] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasEditedNote, setHasEditedNote] = useState(false);
  const [showEmptyNoteModal, setShowEmptyNoteModal] = useState(false);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const [showTranscriptionNotesModal, setShowTranscriptionNotesModal] =useState(false);
  const [showTranscriptionSaveConfirmModal, setShowTranscriptionSaveConfirmModal] =useState(false);
  const [dueError, setDueError] = useState("");
  const [showRecordSessionModal, setShowRecordSessionModal] = useState(false);
  const [showAudioCloseConfirmModal, setShowAudioCloseConfirmModal] =useState(false);
  const [audioUnsafeCloseState, setAudioUnsafeCloseState] = useState(DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);
  const allTreatments = treatmentData?.treatment_plan?.treatments || [];
  const totalPrice = allTreatments.reduce((sum, t) => sum + (t.price || 0), 0);
  const consentBundleId = treatmentData?.id;
  const treatmentPlanId = treatmentData?.treatment_plan_id;

  const [isCheckingExistingAudio, setIsCheckingExistingAudio] = useState(false);
  const [showExistingAudioWarningModal, setShowExistingAudioWarningModal] = useState(false);

  const isTranscriptionReviewUrl = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("transcription") === "true";
  }, [location.search]);

  const todayDate = useMemo(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const showDueError = (message) => {
    setDueError(message);
    setTimeout(() => {
      setDueError("");
    }, 3000);
  };

  const isPastDate = (dateValue) => {
    if (!dateValue) return false;

    const selected = new Date(`${dateValue}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selected < today;
  };

  const getExistingAppointmentDate = () => {
    const possibleDate =
      treatmentData?.appointment_date ||
      treatmentData?.appointmentDate ||
      treatmentData?.appointment?.date ||
      treatmentData?.appointment?.appointment_date ||
      treatmentData?.due_date ||
      "";

    if (!possibleDate) return "";

    return String(possibleDate).split("T")[0].split(" ")[0];
  };

  const getEffectiveAppointmentDate = () => {
    return customDate || getExistingAppointmentDate();
  };

  const validateAppointmentDateBeforeProgress = () => {
    const effectiveDate = getEffectiveAppointmentDate();

    if (effectiveDate && isPastDate(effectiveDate)) {
      showDueError(
        "Appointment date cannot be in the past. Please select today or a future date before continuing."
      );
      toast.error("Please select today or a future appointment date.");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (treatmentData?.clinician) {
      setClinician(treatmentData.clinician.id.toString());
    }
  }, [treatmentData]);

  useEffect(() => {
    const shouldMarkReloadAsEdited = shouldMarkReloadedNoteAsEditedRef.current;

    if (!hasEditedNote || shouldMarkReloadAsEdited) {
      const existingNote = getNotesFromTreatmentData(treatmentData);

      if (existingNote) {
        setNote(existingNote);
        setNoteEditorVersion((version) => version + 1);

        if (shouldMarkReloadAsEdited) {
          setHasEditedNote(true);
          shouldMarkReloadedNoteAsEditedRef.current = false;
        }
      }
    }
  }, [treatmentData, hasEditedNote]);

  useEffect(() => {
    if (!isTranscriptionReviewUrl) return;
    if (transcriptionModalOpenedRef.current) return;
    if (!consentBundleId) return;

    const existingNote = getNotesFromTreatmentData(treatmentData);
    if (existingNote && !hasEditedNote) {
      setNote(existingNote);
      setNoteEditorVersion((version) => version + 1);
    }

    setShowTranscriptionNotesModal(true);
    transcriptionModalOpenedRef.current = true;
  }, [
    isTranscriptionReviewUrl,
    consentBundleId,
    treatmentData,
    hasEditedNote,
  ]);

  const patient = treatmentData?.patient || {};
  const clinicianObj = treatmentData?.clinician || {};

  const reloadTreatmentData = async () => {
    try {
      const res = await axios.get(`consent_bundle/${consentBundleId}`);
      setTreatmentData(res.data);
      console.log("Consent bundle reloaded");
      return res.data;
    } catch (err) {
      console.error("Error reloading consent bundle:", err);
      return null;
    }
  };

  const generatePatientLetter = useMutation(
    async () => {
      if (!note || note.trim() === "") {
        toast.info("No patient note provided — skipping letter generation.");
        onNext();
        return null;
      }

      setIsGenerating(true);
      await new Promise((resolve) => setTimeout(resolve, 500));

      let attempt = 0;
      const maxAttempts = 3;
      let lastError;

      while (attempt < maxAttempts) {
        try {
          const base = process.env.REACT_APP_BASE_URL_V2;
          if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

          const endpoint = `${base}consent_bundle/${consentBundleId}/generate_patient_letter`;

          const response = await axios.put(endpoint, {
            patient_notes: note || "",
          });

          return response;
        } catch (err) {
          attempt += 1;
          lastError = err;
          console.warn(`Attempt ${attempt} failed, retrying...`);
          toast.error("Failed to generate, retrying...");
          if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
      }

      throw lastError;
    },
    {
      onSuccess: async (response) => {
        if (!response) return;

        toast.success("Patient consent letter generated successfully!");
        console.log("Patient letter generated");
        setIsGenerating(false);

        try {
          await reloadTreatmentData();
          console.log("Treatment data refreshed after letter generation");
        } catch (err) {
          console.error("Error refreshing data after letter generation:", err);
        }

        onNext();
      },
      onError: (error) => {
        console.error("All retries failed:", error);
        toast.error(
          "Failed to generate the letter after 3 attempts. Proceeding to next step."
        );
        setIsGenerating(false);
        onNext();
      },
      onSettled: () => {
        setIsGenerating(false);
      },
    }
  );

  const saveTranscriptionNotes = useMutation(
    async () => {
      if (!consentBundleId) {
        throw new Error("Missing consent bundle ID.");
      }

      if (!treatmentPlanId) {
        throw new Error("Missing treatment plan ID.");
      }

      const cleanNote = note.trim();

      if (!cleanNote) {
        throw new Error("Notes cannot be empty.");
      }

      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = `${base}consent_bundle/${consentBundleId}/generate_patient_letter`;

      const response = await axios.put(endpoint, {
        patient_notes: cleanNote,
      });

      await axios.post(
        `${base}audio-recording/archive_audio_record/${treatmentPlanId}`
      );

      return response;
    },
    {
      onSuccess: async () => {
        toast.success("Transcription notes saved successfully.");

        const reviewedNote = note.trim();
        setHasEditedNote(false);
        setShowTranscriptionSaveConfirmModal(false);
        setShowTranscriptionNotesModal(false);

        const refreshedData = await reloadTreatmentData();
        const refreshedNote = getNotesFromTreatmentData(refreshedData);

        setNote(refreshedNote || reviewedNote);
        setNoteEditorVersion((version) => version + 1);
      },
      onError: (error) => {
        console.error("Error saving transcription notes:", error);

        toast.error(
          error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to save transcription notes. Please try again."
        );
      },
    }
  );

  const setPractitioner = useMutation(
    ({ practitioner_id, load_clinician_templates }) =>
      axios.put(
        `/consent_bundle/${consentBundleId}/set_practitioner_for_consent_bundle`,
        { practitioner_id, load_clinician_templates }
      ),
    {
      onSuccess: (_data, variables) => {
        console.log("Practitioner updated successfully");

        if (variables?.should_generate_letter) {
          generatePatientLetter.mutate();
        } else {
          onNext();
        }
      },
      onError: (error) => {
        console.error("Error setting practitioner:", error);
        toast.error("Failed to set clinician. Please try again.");
      },
    }
  );

  const updateAppointment = useMutation(
    (payload) => {
      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = `${base}consent_bundle/${consentBundleId}/update_appointment_date`;
      return axios.put(endpoint, payload);
    },
    {
      onSuccess: () => {
        console.log("Appointment date updated successfully");
        setDueError("");
        reloadTreatmentData();
      },
      onError: (err) => {
        console.error("Error updating appointment date:", err);
        showDueError("Failed to update appointment date. Please select a valid date.");
      },
    }
  );

  const handleDueOptionChange = (value) => {
    setDueOption(value);
    setCustomDate("");
    setDueError("");

    if (value) {
      updateAppointment.mutate({ auto_appointment_date_in: value });
    }
  };

  const handleCustomDateChange = (value) => {
    setCustomDate(value);
    setDueOption("");

    if (!value) {
      setDueError("");
      return;
    }

    if (isPastDate(value)) {
      showDueError("Please select today or a future date.");
      return;
    }

    setDueError("");
    updateAppointment.mutate({ appointment_date: `${value} 09:00:00` });
  };

  const handleTranscriptionSaveClick = () => {
    if (!consentBundleId) {
      toast.error("Missing consent bundle ID.");
      return;
    }

    if (!note.trim()) {
      toast.error("Please add notes before saving.");
      return;
    }

    setShowTranscriptionSaveConfirmModal(true);
  };

  const handleTranscriptionConfirmedSave = () => {
    setShowTranscriptionSaveConfirmModal(false);
    saveTranscriptionNotes.mutate();
  };

  const handleNext = () => {
    if (!consentBundleId) {
      toast.error("Missing consent bundle ID.");
      return;
    }

    if (!clinician) {
      toast.error("Please select a clinician before proceeding.");
      return;
    }

    if (!validateAppointmentDateBeforeProgress()) {
      return;
    }

    if (!note || note.trim() === "") {
      setShowEmptyNoteModal(true);
      return;
    }

    if (hasEditedNote) {
      setShowConfirmModal(true);
      return;
    }

    setPractitioner.mutate({
      practitioner_id: clinician,
      load_clinician_templates: applyTemplate,
      should_generate_letter: false,
    });
  };

  const handleConfirmProceed = () => {
    setShowConfirmModal(false);
    setHasEditedNote(false);

    if (!validateAppointmentDateBeforeProgress()) {
      return;
    }

    if (!note.trim()) {
      toast.info("No patient note provided — skipping letter generation.");
      onNext();
      return;
    }

    setPractitioner.mutate({
      practitioner_id: clinician,
      load_clinician_templates: applyTemplate,
      should_generate_letter: true,
    });
  };

  const handleCancelProceed = () => {
    setShowConfirmModal(false);
  };

  const isBusy =
    isGenerating ||
    isCheckingExistingAudio ||
    setPractitioner.isPending ||
    generatePatientLetter.isPending ||
    updateAppointment.isPending ||
    saveTranscriptionNotes.isPending;

  const isNextDisabled = isBusy || (customDate && isPastDate(customDate));

  const forceCloseRecordSessionModal = () => {
    setShowAudioCloseConfirmModal(false);
    setShowRecordSessionModal(false);
    setAudioUnsafeCloseState(DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);
  };

  const requestCloseRecordSessionModal = () => {
    if (audioUnsafeCloseState.isUnsafe) {
      setShowAudioCloseConfirmModal(true);
      return;
    }

    forceCloseRecordSessionModal();
  };

  const handleRecordSessionOverlayMouseDown = (event) => {
    if (event.target !== event.currentTarget) return;
    requestCloseRecordSessionModal();
  };

  const handleAudioUnsafeStateChange = (nextState) => {
    setAudioUnsafeCloseState(nextState || DEFAULT_AUDIO_UNSAFE_CLOSE_STATE);

    if (!nextState?.isUnsafe) {
      setShowAudioCloseConfirmModal(false);
    }
  };

  useEffect(() => {
    if (!showRecordSessionModal || !audioUnsafeCloseState.isUnsafe) return;

    const unloadMessage =
      audioUnsafeCloseState.message ||
      "Audio recording or upload is still running. Leaving now may interrupt it.";

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = unloadMessage;
      return unloadMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    showRecordSessionModal,
    audioUnsafeCloseState.isUnsafe,
    audioUnsafeCloseState.message,
  ]);

  useEffect(() => {
    if (!showRecordSessionModal) return;

    const handleEscapeClose = (event) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      requestCloseRecordSessionModal();
    };

    window.addEventListener("keydown", handleEscapeClose);

    return () => {
      window.removeEventListener("keydown", handleEscapeClose);
    };
  }, [showRecordSessionModal, audioUnsafeCloseState]);


const handleAudioPatientNoteSaved = async ({ editedPatientNote } = {}) => {
  const savedNote = normalizeNoteValue(editedPatientNote);
  shouldMarkReloadedNoteAsEditedRef.current = true;

  if (savedNote) {
    setNote(savedNote);
    setNoteEditorVersion((version) => version + 1);
  }

  const refreshedData = await reloadTreatmentData();
  const refreshedNote = getNotesFromTreatmentData(refreshedData);
  const nextNote = refreshedNote || savedNote || note;

  if (nextNote) {
    setNote(nextNote);
  }

  setNoteEditorVersion((version) => version + 1);
  setHasEditedNote(true);
  shouldMarkReloadedNoteAsEditedRef.current = false;
  toast.success(
    "AI patient letter generator reloaded. The updated note will regenerate the letter on the next step."
  );
};

const handleOpenRecordSession = async () => {
  if (!treatmentPlanId) {
    toast.error("Missing treatment plan ID.");
    return;
  }

  const base = process.env.REACT_APP_BASE_URL_V2;
  if (!base) {
    toast.error("REACT_APP_BASE_URL_V2 is not set.");
    return;
  }

  setIsCheckingExistingAudio(true);

  try {
    const { data } = await axios.get(
      `${base}audio-recording/check_treatment_plan_id/${treatmentPlanId}`
    );

    const shouldShowReplaceWarning = data?.result === false;

    if (shouldShowReplaceWarning) {
      setShowExistingAudioWarningModal(true);
      return;
    }

    setShowRecordSessionModal(true);
  } catch (error) {
    console.error("Error checking existing audio record:", error);

    toast.error(
      error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to check existing audio record. Please try again."
    );
  } finally {
    setIsCheckingExistingAudio(false);
  }
};



  return (
    <div className={styles.page}>
      <section className={styles.bundleInfoCard}>
        <div className={styles.bundleInfoRow}>
          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Consent bundle ID</span>
            <div className={styles.bundleValueRow}>
              <span className={styles.bundleValue}>{consentBundleId || "—"}</span>

              {consentBundleId && (
                <button
                  type="button"
                  className={styles.copyIconBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(String(consentBundleId));
                    toast.success("Consent bundle ID copied");
                  }}
                  aria-label="Copy consent bundle ID"
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>

          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Treatment plan ID</span>
            <div className={styles.bundleValueRow}>
              <span className={styles.bundleValue}>
                {treatmentData?.treatment_plan_id || "—"}
              </span>

              {treatmentData?.treatment_plan_id && (
                <button
                  type="button"
                  className={styles.copyIconBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      String(treatmentData.treatment_plan_id)
                    );
                    toast.success("Treatment plan ID copied");
                  }}
                  aria-label="Copy treatment plan ID"
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>

          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Code</span>
            <span className={styles.bundleValue}>{treatmentData?.code || "—"}</span>
          </div>

          <div className={styles.bundleItem}>
            <span className={styles.bundleLabel}>Version</span>
            <span className={styles.bundleValue}>
              v{treatmentData?.version_no || "2.0.0"}
            </span>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Clinician selection</h3>
        <label className={styles.field}>
          <span className={styles.label}>Select clinician</span>
          <div className={styles.selectWrap}>
            <select
              className={styles.select}
              value={clinician}
              onChange={(e) => setClinician(e.target.value)}
              disabled={isBusy}
            >
              <option value="">Choose a clinician</option>
              {clinicianObj?.id && (
                <option value={clinicianObj.id}>
                  {clinicianObj.name} ({clinicianObj.role})
                </option>
              )}
            </select>
            <span className={styles.selectChevron} aria-hidden>
              ▾
            </span>
          </div>
          <small className={styles.helpText}>
            Templates will be loaded based on what the clinician has created for the
            practice.
          </small>
        </label>

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={applyTemplate}
            onChange={(e) => setApplyTemplate(e.target.checked)}
            disabled={isBusy}
          />
          <span>Apply this clinician’s consent template</span>
        </label>
      </section>

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Patient information</h3>
        <div className={styles.grid}>
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input
              className={styles.input}
              value={`${patient.title || ""} ${patient.first_name || ""} ${
                patient.last_name || ""
              }`}
              readOnly
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Mobile</span>
            <input
              className={styles.input}
              value={patient.mobile_phone || "N/A"}
              readOnly
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input
              className={styles.input}
              value={patient.email_address || "N/A"}
              readOnly
            />
          </label>
          <label className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span className={styles.label}>Address</span>
            <input
              className={styles.input}
              value={
                patient.address_line_1 ||
                patient.address_line_2 ||
                patient.town ||
                patient.postcode ||
                "No address provided"
              }
              readOnly
            />
          </label>
        </div>
      </section>

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Due</h3>
        <label className={styles.field}>
          <span className={styles.label}>Schedule appointment date</span>
          <div className={styles.dueRow}>
            <select
              className={styles.select}
              value={dueOption}
              onChange={(e) => handleDueOptionChange(e.target.value)}
              disabled={isBusy}
            >
              <option value="1 day">1 Day</option>
              <option value="1 week">1 Week</option>
              <option value="2 weeks">2 Weeks</option>
              <option value="3 weeks">3 Weeks</option>
              <option value="1 month">1 Month</option>
            </select>

            <span className={styles.orText}>or</span>

            <input
              type="date"
              className={styles.input}
              value={customDate}
              min={todayDate}
              onChange={(e) => handleCustomDateChange(e.target.value)}
              disabled={isBusy}
            />
          </div>

          {dueError ? (
            <small
              style={{
                color: "#d32f2f",
                display: "block",
                marginTop: "8px",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {dueError}
            </small>
          ) : (
            <small className={styles.helpText}>
              Choose a specific date or let the system calculate automatically.
            </small>
          )}
        </label>
      </section>

      <div className={styles.subBlock}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h4 className={styles.subTitle}>Record session</h4>
            <p
              style={{
                margin: "6px 0 0",
                color: "#64748b",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Record the patient consultation or clinical discussion before generating
              the AI patient letter.
            </p>
          </div>

          <button
            type="button"
            className={flow.btnPrimary}
            onClick={handleOpenRecordSession}
            disabled={isBusy}
            style={{
              background: "#111827",
              minWidth: "160px",
            }}
          >
            {isCheckingExistingAudio ? "Checking..." : "Record session"}
          </button>
        </div>
      </div>

      <div className={styles.subBlock}>
        <h4 className={styles.subTitle}>AI patient letter generator</h4>
        <div className={styles.noticeBox}>
          <span className={styles.noticeIcon}>ℹ️</span>
          <span className={styles.noticeText}>
            No need to worry, all your data is GDPR compliant and fully encrypted
          </span>
        </div>

        <div className={styles.textareaWrapper}>
          <PlainPatientNoteEditor
            key={`main-note-editor-${noteEditorVersion}`}
            value={note}
            minHeight={300}
            placeholder={`(Add patient note here)
MH: Prev ortho + veneers. Wants whiter, wider smile with lower tooth show. Prefers veneers.

O/E: UL12 crowns, UR12 veneers, Class II div I, full overbite, missing UL3, TMJ click LHS. OH good, mild gingivitis.

Dx/Advice: Local gingivitis – improve OH, 3–6 m recalls.`}
            ariaLabel="AI patient letter generator patient notes"
            disabled={isBusy}
            onChange={(nextValue) => {
              setNote(nextValue);
              setHasEditedNote(true);
            }}
          />

          {isGenerating && (
            <div className={styles.generatingOverlay}>
              <div className={styles.generatingContainer}>
                <Lottie
                  animationData={generatingAnim}
                  loop
                  autoplay
                  style={{ width: 220, height: 220 }}
                />
                <p className={styles.generatingText}>Generating letter...</p>
              </div>
            </div>
          )}
        </div>

        <div className={styles.badgesRow}>
          <span className={`${styles.badge} ${styles.badgeSoft}`}>
            Version: <strong>v{treatmentData?.version_no || "2.0.0"}</strong>
          </span>
          <span className={`${styles.badge} ${styles.badgeOutline}`}>
            Mode: <strong>{treatmentData?.mode || "Consent_Bundle"}</strong>
          </span>
        </div>
      </div>

      {allTreatments.length > 0 && (
        <div className={styles.subBlock}>
          <h4 className={styles.subTitle}>Treatment Plans</h4>

          <div className={styles.bubbleContainer}>
            {allTreatments.map((t) => (
              <div key={t.id} className={styles.treatmentBubble}>
                <span className={styles.bubbleTitle}>{t.nomenclature}</span>

                {t.tooth && t.tooth !== "{}" && (
                  <span className={styles.bubbleTooth}>{t.tooth}</span>
                )}

                <span className={styles.bubblePlan}>
                  Payment Plan: {t.payment_plan}
                </span>

                <span className={styles.bubblePrice}>£{t.price}</span>
              </div>
            ))}
          </div>

          <div className={styles.totalPriceUnderBubbles}>
            <span>Total: </span>
            <strong>£{totalPrice.toFixed(2)}</strong>
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <button className={flow.btnOutline} onClick={onBack} disabled={isBusy}>
          Back
        </button>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className={flow.btnPrimary}
            onClick={handleNext}
            disabled={isNextDisabled}
            style={{ background: "#e25a1bff" }}
          >
            {setPractitioner.isPending
              ? "Saving clinician..."
              : isGenerating
              ? "Generating letter..."
              : "Next"}
          </button>
        </div>
      </div>

      {showTranscriptionNotesModal && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modal}
            style={{
              maxWidth: "900px",
              width: "95%",
              maxHeight: "90vh",
              overflowY: "auto",
              textAlign: "left",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "14px",
                marginBottom: "16px",
                paddingBottom: "14px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>Review transcription notes</h3>
                <p
                  style={{
                    margin: "6px 0 0",
                    color: "#64748b",
                    fontSize: "14px",
                    lineHeight: "1.5",
                  }}
                >
                  Review and edit the notes before saving them to the patient letter
                  generator.
                </p>
              </div>

              <button
                type="button"
                className={flow.btnOutline}
                onClick={() => setShowTranscriptionNotesModal(false)}
                disabled={saveTranscriptionNotes.isPending}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 10px",
                borderRadius: "999px",
                background: "#f3f4f6",
                color: "#111827",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "12px",
              }}
            >
              <span>Consent bundle ID:</span>
              <strong>{consentBundleId || "—"}</strong>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Patient notes</span>
              <PlainPatientNoteEditor
                key={`transcription-note-editor-${noteEditorVersion}`}
                value={note}
                minHeight={320}
                placeholder="Transcription notes will appear here."
                ariaLabel="Review transcription patient notes"
                className={styles.transcriptionRichNoteEditor}
                disabled={saveTranscriptionNotes.isPending}
                onChange={(nextValue) => {
                  setNote(nextValue);
                  setHasEditedNote(true);
                }}
              />
            </div>

            <div className={styles.modalActions} style={{ marginTop: "18px" }}>
              <button
                type="button"
                className={flow.btnOutline}
                onClick={() => setShowTranscriptionNotesModal(false)}
                disabled={saveTranscriptionNotes.isPending}
              >
                Cancel
              </button>

              <button
                type="button"
                className={flow.btnPrimary}
                onClick={handleTranscriptionSaveClick}
                disabled={saveTranscriptionNotes.isPending || !note.trim()}
              >
                {saveTranscriptionNotes.isPending
                  ? "Saving..."
                  : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTranscriptionSaveConfirmModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h4 className={styles.title}>Confirm save</h4>

            <p className={styles.message}>
              This will save the reviewed transcription notes and regenerate the
              patient consent letter using the updated notes.
              <br />
              <br />
              Do you want to continue?
            </p>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowTranscriptionSaveConfirmModal(false)}
                className={flow.btnOutline}
                disabled={saveTranscriptionNotes.isPending}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleTranscriptionConfirmedSave}
                className={flow.btnPrimary}
                disabled={saveTranscriptionNotes.isPending}
              >
                {saveTranscriptionNotes.isPending
                  ? "Saving..."
                  : "Yes, Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h4 className={styles.title}>Confirm change</h4>

            <p className={styles.message}>
              You edited the patient notes. Continuing will regenerate the consent
              letter and update its content based on your changes.
              <br />
              <br />
              Would you like to create a new updated letter?
            </p>

            <div className={styles.modalActions}>
              <button onClick={handleCancelProceed} className={flow.btnOutline}>
                Cancel
              </button>

              <button
                className={flow.btnOutline}
                onClick={() => {
                  if (!validateAppointmentDateBeforeProgress()) {
                    return;
                  }

                  setShowEmptyNoteModal(false);
                  onNext();
                }}
              >
                Proceed without letter generation
              </button>

              <button onClick={handleConfirmProceed} className={flow.btnPrimary}>
                Yes, proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmptyNoteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h4>No patient note</h4>

            <p>
              There is no information to generate a patient letter.
              <br />
              Add a note now or continue without generating a letter.
            </p>

            <button
              onClick={() => {
                setShowEmptyNoteModal(false);
                setShowExampleModal(true);
              }}
              className={styles.exampleBtn}
            >
              <InfoOutlinedIcon className={styles.infoPulseIcon} />
              <span>View example patient note</span>
            </button>

            <div className={styles.reassureBox}>
              <InfoOutlinedIcon className={styles.reassureIcon} />
              <span>
                You can always come back and generate the patient letter later if
                needed.
              </span>
            </div>

            <div className={styles.modalActions}>
              <button
                className={flow.btnOutline}
                onClick={() => {
                  setShowEmptyNoteModal(false);
                  const el = document.querySelector(`.${styles.richNoteEditor}`);
                  if (el) el.focus();
                }}
              >
                Add note
              </button>

              <button
                className={flow.btnPrimary}
                onClick={() => {
                  if (!validateAppointmentDateBeforeProgress()) {
                    setShowEmptyNoteModal(false);
                    return;
                  }

                  setShowEmptyNoteModal(false);
                  onNext();
                }}
              >
                Proceed without letter
              </button>
            </div>
          </div>
        </div>
      )}

      {showExampleModal && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modal}
            style={{
              maxWidth: "650px",
              maxHeight: "75vh",
              overflowY: "auto",
              padding: "22px",
              borderRadius: "14px",
              background: "#fff",
              boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
              textAlign: "left",
            }}
          >
            <h3
              style={{
                margin: "0 0 14px 0",
                fontWeight: 600,
                fontSize: "18px",
                textAlign: "left",
              }}
            >
              Example patient note
            </h3>

            <p
              style={{
                whiteSpace: "pre-line",
                lineHeight: "1.55",
                fontSize: "14px",
                color: "#444",
                background: "#f9fafb",
                padding: "16px",
                borderRadius: "10px",
                border: "1px solid #eee",
                textAlign: "left",
              }}
            >
              <strong>MH:</strong> Checked PCO: had a lot of treatment in the past,
              such as ortho as a child, veneers as an adult. Wants a better smile as
              he hasn’t reached his ideal look. Lower teeth don’t show; prefers lower
              tooth display. Wants a wider and whiter smile. Avoids Invisalign (long
              treatment) and composite bonding (short lifespan). Interested in veneers.
              Aware LLQ bridge may be an issue.
              {"\n\n"}
              <strong>SH:</strong> NAD
              {"\n\n"}
              <strong>O/E:</strong> UL12 crowns, UR12 veneers, incisal wear, Class II
              div I, heavy overbite with no lower tooth display. Narrow smile, UL3
              missing. TMJ click on LHS. Oral cancer screen: NAD. OH: Good. Mild
              gingivitis.
              {"\n\n"}
              <strong>Diagnosis:</strong> Chronic localised gingivitis.
              {"\n\n"}
              <strong>Advice:</strong> OHIG – improve brushing time + add floss/TePe
              brushes at night. Recommend 3–6 month cleans.
              {"\n\n"}
              <strong>Patient has chosen:</strong> Veneers.
            </p>

            <div
              className={styles.modalActions}
              style={{ justifyContent: "flex-start", marginTop: "16px" }}
            >
              <button
                className={flow.btnPrimary}
                onClick={() => setShowExampleModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecordSessionModal && (
        <div
          className={styles.modalOverlay}
          onMouseDown={handleRecordSessionOverlayMouseDown}
        >
          <div
            className={styles.modal}
            style={{
              maxWidth: "900px",
              width: "95%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginBottom: "14px",
                paddingBottom: "12px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>Record session</h3>

                <div
                  style={{
                    marginTop: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 10px",
                    borderRadius: "999px",
                    background: "#f3f4f6",
                    color: "#111827",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  <span>Consent bundle ID:</span>
                  <strong>{consentBundleId || "—"}</strong>
                </div>
              </div>

              <button
                type="button"
                className={flow.btnOutline}
                onClick={requestCloseRecordSessionModal}
              >
                Close
              </button>
            </div>

            <AudioRecorder
              consentBundleId={consentBundleId}
              treatmentPlanId={treatmentPlanId}
              apiBaseUrl={process.env.REACT_APP_BASE_URL_V2}
              onRequestClose={forceCloseRecordSessionModal}
              onUnsafeStateChange={handleAudioUnsafeStateChange}
              onPatientNoteSaved={handleAudioPatientNoteSaved}
            />
          </div>
        </div>
      )}

      {showRecordSessionModal && showAudioCloseConfirmModal && (
        <div
          className={styles.modalOverlay}
          style={{ zIndex: 9999 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowAudioCloseConfirmModal(false);
            }
          }}
        >
          <div
            className={styles.modal}
            style={{
              maxWidth: "460px",
              width: "92%",
              textAlign: "left",
            }}
          >
            <h4 className={styles.title}>
              {audioUnsafeCloseState.title || "Audio workflow in progress"}
            </h4>

            <p className={styles.message}>
              {audioUnsafeCloseState.message ||
                "Audio recording or upload is still running. Closing now may interrupt it."}
            </p>

            <div className={styles.reassureBox}>
              <InfoOutlinedIcon className={styles.reassureIcon} />
              <span>
                Choose Stay here to continue safely, or Close anyway if you are sure.
              </span>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={flow.btnOutline}
                onClick={() => setShowAudioCloseConfirmModal(false)}
              >
                Stay here
              </button>

              <button
                type="button"
                className={flow.btnPrimary}
                style={{ background: "#dc2626" }}
                onClick={forceCloseRecordSessionModal}
              >
                Close anyway
              </button>
            </div>
          </div>
        </div>
      )}




{showExistingAudioWarningModal && (
  <div className={styles.modalOverlay}>
    <div
      className={styles.modal}
      style={{
        maxWidth: "520px",
        width: "92%",
        textAlign: "left",
      }}
    >
      <h4 className={styles.title}>Existing audio found</h4>

      <p
        className={styles.message}
        style={{
          fontSize: "17px",
          fontWeight: 700,
          lineHeight: "1.6",
          color: "#111827",
        }}
      >
        A recording already exists for this treatment plan.
        Continuing will replace the old audio, transcript, and patient note.
        <br />
        <br />
        Do you want to continue?
      </p>

      <div
  className={styles.reassureBox}
  style={{
    fontSize: "15px",
    fontWeight: 700,
    lineHeight: "1.5",
  }}
>
  <InfoOutlinedIcon
    className={styles.reassureIcon}
    style={{ fontSize: "22px" }}
  />
  <span>
    This action cannot be undone after the new recording is processed.
  </span>
</div>

      <div className={styles.modalActions}>
        <button
          type="button"
          className={flow.btnOutline}
          onClick={() => setShowExistingAudioWarningModal(false)}
        >
          Cancel
        </button>

        <button
          type="button"
          className={flow.btnPrimary}
          style={{ background: "#dc2626" }}
          onClick={() => {
            setShowExistingAudioWarningModal(false);
            setShowRecordSessionModal(true);
          }}
        >
          Yes, continue
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}
